/**
 * Arbetsresor: resor på delade företagsbilar, med förare och identifiering via app.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth } from 'date-fns';
import { useTranslation } from '@/store/languageStore';
import {
  ArrowLeftIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api from '@/services/api/client';
import { ROUTES } from '@/constants/routes';
import DateInput from '@/components/common/DateInput';

type Presence = 'driver' | 'not_chosen' | 'unknown' | 'single_scan' | 'none';
type IdentificationFilter = 'all' | 'identified' | 'unidentified';

type WorkTrip = {
  tripId: string;
  registrationNumber: string;
  date: string;
  startTime: string;
  endTime: string;
  startAddress: string;
  endAddress: string;
  distanceKm: number;
  durationLabel: string;
  driverName: string | null;
  purpose: string;
  presence: Presence;
  presentNames: string;
};

type ReportData = {
  trips: WorkTrip[];
  vehicles: { vehicleId: number; registrationNumber: string }[];
  summary: {
    tripCount: number;
    identifiedCount: number;
    unidentifiedCount: number;
    distanceKm: number;
  };
};

const emptySummary = { tripCount: 0, identifiedCount: 0, unidentifiedCount: 0, distanceKm: 0 };

function presenceLabel(presence: Presence, t: (key: string) => string): string {
  switch (presence) {
    case 'driver':
      return t('reports.workTrips.presenceDriver');
    case 'not_chosen':
      return t('reports.workTrips.presenceNotChosen');
    case 'single_scan':
      return t('reports.workTrips.presenceSingleScan');
    case 'unknown':
      return t('reports.workTrips.presenceUnknown');
    case 'none':
      return t('reports.workTrips.presenceNone');
    default: {
      const unreachable: never = presence;
      return unreachable;
    }
  }
}

function purposeLabel(purpose: string, t: (key: string) => string): string {
  if (purpose === 'business') return t('reports.drivingLog.business');
  if (purpose === 'private') return t('reports.drivingLog.private');
  if (!purpose) return t('reports.drivingLog.undefined');
  return purpose;
}

const WorkTripsReport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(() => format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [vehicleId, setVehicleId] = useState('');
  const [identification, setIdentification] = useState<IdentificationFilter>('all');
  const [vehicles, setVehicles] = useState<ReportData['vehicles']>([]);
  const [trips, setTrips] = useState<WorkTrip[]>([]);
  const [summary, setSummary] = useState(emptySummary);
  const [isLoading, setIsLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/reports/work-trips', {
        params: {
          startDate,
          endDate,
          vehicleId: vehicleId || undefined,
          identification,
        },
      });
      if (!response.data.success) {
        toast.error(response.data.error || t('reports.workTrips.fetchError'));
        return;
      }
      const data = response.data.data as ReportData;
      setTrips(data.trips || []);
      setVehicles(data.vehicles || []);
      setSummary(data.summary || emptySummary);
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || t('reports.workTrips.fetchError'));
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, vehicleId, identification, t]);

  useEffect(() => {
    void fetchReport();
  }, [fetchReport]);

  const exportCsv = () => {
    const header = [
      t('reports.workTrips.colVehicle'),
      t('reports.workTrips.colDate'),
      t('reports.workTrips.colTime'),
      t('reports.workTrips.colFrom'),
      t('reports.workTrips.colTo'),
      t('reports.workTrips.colDistance'),
      t('reports.workTrips.colDuration'),
      t('reports.workTrips.colDriver'),
      t('reports.workTrips.colIdentification'),
      t('reports.workTrips.colPresent'),
      t('reports.workTrips.colPurpose'),
    ];
    const lines = trips.map((trip) => [
      trip.registrationNumber,
      trip.date,
      `${trip.startTime} - ${trip.endTime}`,
      trip.startAddress,
      trip.endAddress,
      String(trip.distanceKm),
      trip.durationLabel,
      trip.driverName || t('reports.workTrips.noDriver'),
      presenceLabel(trip.presence, t),
      trip.presentNames,
      purposeLabel(trip.purpose, t),
    ].map((cell) => `"${cell.replace(/"/g, '""')}"`).join(';'));
    const blob = new Blob([`\uFEFF${[header.join(';'), ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `arbetsresor-${startDate}-${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(t('reports.workTrips.exportSuccess'));
  };

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full overflow-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <button type="button" onClick={() => navigate(ROUTES.SYSTEM_REPORTS)} className="p-2 hover:bg-bgElevated rounded-lg">
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <ClipboardDocumentListIcon className="w-6 h-6 text-primary" />
            {t('reports.workTrips.title')}
          </h1>
        </div>
        <p className="text-textSecondary">{t('reports.workTrips.description')}</p>
      </div>

      <div className="bg-bgElevated shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-textDefault mb-4">{t('reports.workTrips.filtersTitle')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-textDefault mb-2">{t('reports.workTrips.vehicleLabel')}</label>
            <select
              value={vehicleId}
              onChange={(event) => setVehicleId(event.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-bgBase text-textDefault"
            >
              <option value="">{t('reports.workTrips.allVehicles')}</option>
              {vehicles.map((vehicle) => (
                <option key={vehicle.vehicleId} value={vehicle.vehicleId}>
                  {vehicle.registrationNumber}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-textDefault mb-2">
              <CalendarIcon className="w-4 h-4 inline mr-1" />
              {t('reports.workTrips.fromDate')}
            </label>
            <DateInput value={startDate} onChange={(event) => setStartDate(event.target.value)} className="w-full px-3 py-2 border border-border rounded-md bg-bgBase text-textDefault" />
          </div>
          <div>
            <label className="block text-sm font-medium text-textDefault mb-2">{t('reports.workTrips.toDate')}</label>
            <DateInput value={endDate} onChange={(event) => setEndDate(event.target.value)} className="w-full px-3 py-2 border border-border rounded-md bg-bgBase text-textDefault" />
          </div>
          <div>
            <label className="block text-sm font-medium text-textDefault mb-2">{t('reports.workTrips.identificationLabel')}</label>
            <select
              value={identification}
              onChange={(event) => setIdentification(event.target.value as IdentificationFilter)}
              className="w-full px-3 py-2 border border-border rounded-md bg-bgBase text-textDefault"
            >
              <option value="all">{t('reports.workTrips.identificationAll')}</option>
              <option value="identified">{t('reports.workTrips.identificationIdentified')}</option>
              <option value="unidentified">{t('reports.workTrips.identificationUnidentified')}</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <button type="button" onClick={() => { void fetchReport(); }} disabled={isLoading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {isLoading ? t('common.loading') : t('reports.workTrips.fetch')}
          </button>
          <button type="button" onClick={exportCsv} disabled={trips.length === 0} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2">
            <DocumentArrowDownIcon className="w-4 h-4" />
            {t('common.exportCsv')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-bgElevated rounded-lg border border-border p-4">
          <div className="text-sm text-textSecondary">{t('reports.workTrips.tripCount')}</div>
          <div className="text-2xl font-semibold text-textDefault">{summary.tripCount}</div>
        </div>
        <div className="bg-bgElevated rounded-lg border border-border p-4">
          <div className="text-sm text-textSecondary">{t('reports.workTrips.identifiedCount')}</div>
          <div className="text-2xl font-semibold text-textDefault">{summary.identifiedCount}</div>
        </div>
        <div className="bg-bgElevated rounded-lg border border-border p-4">
          <div className="text-sm text-textSecondary">{t('reports.workTrips.unidentifiedCount')}</div>
          <div className="text-2xl font-semibold text-textDefault">{summary.unidentifiedCount}</div>
        </div>
        <div className="bg-bgElevated rounded-lg border border-border p-4">
          <div className="text-sm text-textSecondary">{t('reports.workTrips.totalDistance')}</div>
          <div className="text-2xl font-semibold text-textDefault">{summary.distanceKm.toFixed(1)} km</div>
        </div>
      </div>

      <div className="bg-bgElevated rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-bgContainer border-b border-border">
              <tr>
                <th className="px-2 py-2 text-left font-medium text-textSecondary">{t('reports.workTrips.colVehicle')}</th>
                <th className="px-2 py-2 text-left font-medium text-textSecondary">{t('reports.workTrips.colDate')}</th>
                <th className="px-2 py-2 text-left font-medium text-textSecondary">{t('reports.workTrips.colTime')}</th>
                <th className="px-2 py-2 text-left font-medium text-textSecondary">{t('reports.workTrips.colFrom')}</th>
                <th className="px-2 py-2 text-left font-medium text-textSecondary">{t('reports.workTrips.colTo')}</th>
                <th className="px-2 py-2 text-right font-medium text-textSecondary">{t('reports.workTrips.colDistance')}</th>
                <th className="px-2 py-2 text-right font-medium text-textSecondary">{t('reports.workTrips.colDuration')}</th>
                <th className="px-2 py-2 text-left font-medium text-textSecondary">{t('reports.workTrips.colDriver')}</th>
                <th className="px-2 py-2 text-left font-medium text-textSecondary">{t('reports.workTrips.colIdentification')}</th>
                <th className="px-2 py-2 text-left font-medium text-textSecondary">{t('reports.workTrips.colPresent')}</th>
                <th className="px-2 py-2 text-left font-medium text-textSecondary">{t('reports.workTrips.colPurpose')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {trips.map((trip) => (
                <tr key={trip.tripId} className="hover:bg-bgBase/50">
                  <td className="px-2 py-2 text-textPrimary whitespace-nowrap">{trip.registrationNumber}</td>
                  <td className="px-2 py-2 text-textPrimary whitespace-nowrap">{trip.date}</td>
                  <td className="px-2 py-2 text-textSecondary whitespace-nowrap">{trip.startTime} → {trip.endTime}</td>
                  <td className="px-2 py-2 text-textPrimary max-w-[180px] truncate" title={trip.startAddress}>{trip.startAddress || '-'}</td>
                  <td className="px-2 py-2 text-textPrimary max-w-[180px] truncate" title={trip.endAddress}>{trip.endAddress || '-'}</td>
                  <td className="px-2 py-2 text-right font-medium text-blue-600 whitespace-nowrap">{trip.distanceKm.toFixed(1)} km</td>
                  <td className="px-2 py-2 text-right text-textSecondary whitespace-nowrap">{trip.durationLabel}</td>
                  <td className="px-2 py-2 text-textPrimary whitespace-nowrap">{trip.driverName || t('reports.workTrips.noDriver')}</td>
                  <td className="px-2 py-2 text-textPrimary whitespace-nowrap">{presenceLabel(trip.presence, t)}</td>
                  <td className="px-2 py-2 text-textSecondary max-w-[220px] truncate" title={trip.presentNames}>{trip.presentNames || '-'}</td>
                  <td className="px-2 py-2 text-textSecondary whitespace-nowrap">{purposeLabel(trip.purpose, t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {trips.length === 0 && !isLoading ? (
          <p className="p-6 text-textSecondary">{t('reports.workTrips.empty')}</p>
        ) : null}
      </div>
    </div>
  );
};

export default WorkTripsReport;
